import { Component, Input, Output, EventEmitter, inject } from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { SessionSummary, SessionListItem } from '../colimind-types';

@Component({
  selector: 'colimo-session-list',
  standalone: true,
  imports: [MatTooltipModule, MatIconModule, MatButtonModule, TranslateModule],
  templateUrl: './colimind-session-list.component.html',
  styleUrls: ['./colimind-session-list.component.scss'],
})
export class SessionListComponent {
  @Input() sessions: SessionListItem[] = [];
  @Input() activeSessionId?: string;
  /** Translation key shown when the list is empty -- differs between history and search. */
  @Input() emptyMessageKey = 'colimind.noSessions';
  @Output() selectSession = new EventEmitter<string>();
  @Output() deleteSession = new EventEmitter<string>();
  @Output() deleteAllSessions = new EventEmitter<void>();

  private translate = inject(TranslateService);

  formatDate(iso: string): string {
    const d = new Date(iso);
    // Sessions stored before timestamps became ISO carry a localized display string.
    if (isNaN(d.getTime())) { return iso ?? ''; }

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.floor((startOfToday.getTime() - startOfDate.getTime()) / 86400000);

    let sessionInfoDatePrefix: string;
    if (diffDays === 0) {
      sessionInfoDatePrefix = this.translate.instant('colimind.today');
    } else if (diffDays === 1) {
      sessionInfoDatePrefix = this.translate.instant('colimind.yesterday');
    } else if (diffDays < 7) {
      sessionInfoDatePrefix = d.toLocaleDateString([], { weekday: 'short' });
    } else {
      sessionInfoDatePrefix = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }

    return `${sessionInfoDatePrefix} - ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  msgCount(session: SessionSummary): number {
    return Math.floor(session.message_count / 2);
  }

  /** Session awaiting delete confirmation, or null. Only one row can be pending at a time. */
  pendingDeleteId: string | null = null;
  pendingDeleteAll = false;

  /** First click on the delete icon only arms the confirmation shown inline in the row. */
  askDelete(event: Event, sessionId: string): void {
    event.stopPropagation();
    this.pendingDeleteAll = false;
    this.pendingDeleteId = sessionId;
  }

  confirmDelete(event: Event, sessionId: string): void {
    event.stopPropagation();
    this.pendingDeleteId = null;
    this.deleteSession.emit(sessionId);
  }

  cancelDelete(event: Event): void {
    event.stopPropagation();
    this.pendingDeleteId = null;
    this.pendingDeleteAll = false;
  }

  askDeleteAll(): void {
    this.pendingDeleteId = null;
    this.pendingDeleteAll = true;
  }

  confirmDeleteAll(): void {
    this.pendingDeleteAll = false;
    this.deleteAllSessions.emit();
  }
}
